import axios from 'axios';
import * as Sentry from '@sentry/react';
import config from '@/config';
import { IRefreshTokenResponse } from '@/types/auth';
import { GetStorageComponent, SetStorageComponent } from '@/utils/session';
const REFRESH_URL = `${(config.authBaseUrl ?? '').replace(/\/+$/, '')}/api/v1/auth/refresh`;

// Single-flight guard: concurrent 401s share one in-flight refresh request
// instead of triggering a stampede of /auth/refresh calls.
let refreshPromise: Promise<string | null> | null = null;

/** Pulls the raw JWT out of an `Authorization: Bearer <jwt>` header value. */
export function tokenFromAuthHeader(header: unknown): string | null {
  const match = /^Bearer\s+(.+)$/i.exec(typeof header === 'string' ? header : '');
  return match ? match[1] : null;
}

/**
 * Silently obtains a fresh access token using the HttpOnly `refresh_token`
 * cookie (set by the auth service on login and scoped to /api/v1/auth).
 *
 * The refresh token is never sent from JS — the browser attaches the cookie
 * automatically because the call is made `withCredentials`. On success the new
 * access/id tokens are persisted and the access token is returned. On failure
 * (missing/expired cookie, network error) returns `null`.
 *
 * @param staleToken The access token whose rejection triggered this call, when the
 * caller knows it. If storage already holds a different token, a refresh completed
 * while that request was in flight, so the stored token is returned as-is — the
 * single-flight promise only covers callers that overlap the request itself.
 */
export function refreshAccessToken(staleToken?: string | null): Promise<string | null> {
  if (staleToken) {
    const stored = GetStorageComponent('accessToken');
    if (stored && stored !== staleToken) return Promise.resolve(stored);
  }

  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      // Deliberately a bare axios call rather than `authRequest`: that instance imports
      // this module (a cycle), and its response interceptor is the very thing that calls
      // refresh on a 401 — sending refresh through it would nest that handling inside itself.
      const res = await axios.post<IRefreshTokenResponse>(REFRESH_URL, {}, { withCredentials: true });
      const { access_token, id_token } = res.data.data;

      if (access_token) SetStorageComponent('accessToken', access_token);
      if (id_token) SetStorageComponent('idToken', id_token);

      return access_token ?? null;
    } catch (error) {
      // A missing or expired refresh cookie is a normal end of session, not a fault
      // worth an issue. Record context for whatever real error follows instead.
      Sentry.addBreadcrumb({
        category: 'auth',
        level: 'info',
        message: 'access token refresh failed',
        data: { status: axios.isAxiosError(error) ? (error.response?.status ?? 'network-error') : 'unknown' },
      });
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}
